import React, { useState, useEffect, useRef } from "react";
import { X, Mail, ShieldCheck, CheckCircle2, AlertCircle, RefreshCw, Edit2 } from "lucide-react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { sendVerificationOtp, verifyOtp } from "../redux/Thunk/customerThunk";

export default function VerificationModal({
  isOpen,
  onClose,
  customerEmail,
  token,
  onVerificationSuccess
}) {
  if (!isOpen) return null;

  const dispatch = useDispatch();

  const [step, setStep] = useState(1); // 1: Send OTP, 2: Enter OTP
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [emailInput, setEmailInput] = useState(customerEmail || "");
  const [otp, setOtp] = useState(["", "", "", ""]); // 4-digit OTP
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);

  const inputRefs = useRef([]);

  // Reset states when modal is opened/closed or customerEmail changes
  useEffect(() => {
    if (isOpen) {
      setEmailInput(customerEmail || "");
      setStep(1);
      setError("");
      setSuccess(false);
      setOtp(["", "", "", ""]);
      setTimer(30);
      setCanResend(false);
    }
  }, [isOpen, customerEmail]);

  // Countdown timer for Resend OTP
  useEffect(() => {
    let interval;
    if (step === 2 && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  const handleSendOTP = () => {
    if (!emailInput || !emailInput.includes("@")) {
      setError("Please enter a valid email address.");
      toast.error("Invalid email address.");
      return;
    }

    setLoading(true);
    setError("");

    dispatch(sendVerificationOtp({ token, email: emailInput }))
      .unwrap()
      .then((res) => {
        setLoading(false);
        if (res.success) {
          setStep(2);
          setTimer(30);
          setCanResend(false);
          toast.success("Verification OTP sent successfully!");
          // Autofocus first OTP digit
          setTimeout(() => inputRefs.current[0]?.focus(), 100);
        } else {
          setError(res.message || "Failed to send OTP. Please try again.");
          toast.error(res.message || "Failed to send OTP.");
        }
      })
      .catch((err) => {
        setLoading(false);
        const errMsg = err.message || err || "Something went wrong while sending OTP.";
        setError(errMsg);
        toast.error(errMsg);
      });
  };

  const handleResendOTP = () => {
    if (!canResend) return;
    setLoading(true);
    setError("");
    setOtp(["", "", "", ""]);

    dispatch(sendVerificationOtp({ token, email: emailInput }))
      .unwrap()
      .then((res) => {
        setLoading(false);
        if (res.success) {
          setTimer(30);
          setCanResend(false);
          toast.success("OTP resent successfully!");
          // Put focus back on first input
          setTimeout(() => inputRefs.current[0]?.focus(), 50);
        } else {
          setError(res.message || "Failed to resend OTP.");
          toast.error(res.message || "Failed to resend OTP.");
        }
      })
      .catch((err) => {
        setLoading(false);
        const errMsg = err.message || err || "Something went wrong while resending OTP.";
        setError(errMsg);
        toast.error(errMsg);
      });
  };

  const handleOtpChange = (index, value) => {
    if (isNaN(value)) return; // Allow only numbers
    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1); // Keep last char
    setOtp(newOtp);
    setError("");

    // Move to next input if filled
    if (value && index < 3) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      // Focus previous input on backspace if current is empty
      inputRefs.current[index - 1].focus();
    }
  };

  const handleVerify = () => {
    const otpString = otp.join("");
    if (otpString.length < 4) {
      setError("Please enter all 4 digits.");
      toast.error("Please enter all 4 digits.");
      return;
    }

    setLoading(true);
    setError("");

    dispatch(verifyOtp({ token, otp: otpString }))
      .unwrap()
      .then((res) => {
        setLoading(false);
        if (res.success) {
          setSuccess(true);
          toast.success("Account verified successfully!");
          // Auto-close after success presentation
          setTimeout(() => {
            onVerificationSuccess();
            onClose();
          }, 1500);
        } else {
          setError(res.message || "Invalid verification code. Please try again.");
          toast.error(res.message || "Invalid OTP code.");
          setOtp(["", "", "", ""]);
          inputRefs.current[0]?.focus();
        }
      })
      .catch((err) => {
        setLoading(false);
        const errMsg = err.message || err || "Verification failed. Please try again.";
        setError(errMsg);
        toast.error(errMsg);
        setOtp(["", "", "", ""]);
        inputRefs.current[0]?.focus();
      });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 text-left">
      {/* Modal Container */}
      <div className="bg-white rounded-xl border border-border-primary shadow-2xl max-w-md w-full overflow-hidden relative animate-modal-scale">

        {/* Close button */}
        {!success && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-text-muted hover:text-text-primary rounded-full p-1 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* Content Wrapper */}
        <div className="p-6 sm:p-8">

          {/* SUCCESS STATE */}
          {success ? (
            <div className="flex flex-col items-center text-center py-6">
              <div className="w-16 h-16 bg-success-bg/40 rounded-full flex items-center justify-center text-success mb-4 animate-bounce">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <h3 className="text-xl font-bold font-display text-text-primary">Account Verified</h3>
              <p className="text-sm text-text-secondary mt-1">
                You're verified successfully. You can now manage and cancel your orders.
              </p>
              <div className="mt-4 text-xs font-semibold text-success flex items-center space-x-1">
                <span className="h-2 w-2 rounded-full bg-success animate-ping"></span>
                <span>Updating your profile...</span>
              </div>
            </div>
          ) : (
            <div>
              {/* HEADER */}
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-brand-red-light flex items-center justify-center text-brand-red shrink-0">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold font-display text-text-primary">
                    {step === 1 ? "Verify Your Account" : "Enter Verification Code"}
                  </h3>
                  <p className="text-xs text-text-secondary mt-0.5">
                    {step === 1 ? "Secure your customer profile" : "Check your inbox for OTP"}
                  </p>
                </div>
              </div>

              {/* STEP 1: SEND OTP */}
              {step === 1 && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-xs text-text-secondary font-bold uppercase tracking-wider block">
                      Verification Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-text-secondary" />
                      <input
                        type="email"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        placeholder="your-email@example.com"
                        disabled={loading}
                        className="w-full pl-10 pr-4 py-2.5 border border-border-primary rounded-xl focus:outline-none focus:ring-1 focus:ring-brand-red focus:border-brand-red text-sm font-semibold bg-white transition-all text-text-primary"
                      />
                    </div>
                  </div>

                  <p className="text-xs text-text-secondary leading-relaxed">
                    We will send a 4-digit one-time password (OTP) to this address to verify your identity.
                  </p>

                  {error && (
                    <div className="flex items-center space-x-2 p-2.5 bg-brand-red-light/30 border border-brand-red/10 rounded-lg text-brand-red text-xs">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <button
                    onClick={handleSendOTP}
                    disabled={loading}
                    className="w-full py-2.5 px-4 bg-brand-red text-white text-sm font-semibold rounded-lg shadow-sm hover:bg-brand-red-hover active:scale-[0.98] transition-all disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center space-x-2 cursor-pointer"
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Sending Code...</span>
                      </>
                    ) : (
                      <span>Send OTP</span>
                    )}
                  </button>

                  {/* Professional Notice */}
                  <div className="pt-3 border-t border-border-primary/50 text-[11px] text-text-muted leading-relaxed">
                    <p className="flex items-start gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 text-text-secondary shrink-0 mt-0.5" />
                      <span>
                        Verification is required to secure your billing history and authorize meal cancellations.
                      </span>
                    </p>
                  </div>
                </div>
              )}

              {/* STEP 2: ENTER OTP */}
              {step === 2 && (
                <div className="space-y-5">
                  <div>
                    <p className="text-sm text-text-secondary">
                      A code has been sent to <span className="font-semibold text-text-primary">{emailInput}</span>.
                    </p>
                    <p className="text-xs text-text-muted mt-0.5">Please type the 4-digit code below to complete verification.</p>
                  </div>

                  {/* 4 OTP Inputs */}
                  <div className="flex justify-center gap-4 my-2">
                    {otp.map((digit, idx) => (
                      <input
                        key={idx}
                        ref={(el) => (inputRefs.current[idx] = el)}
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(idx, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(idx, e)}
                        disabled={loading}
                        className="w-14 h-16 text-center text-2xl font-bold font-display border border-border-primary rounded-xl focus:outline-none focus:ring-1 focus:ring-brand-red focus:border-brand-red bg-bg-secondary/40 transition-all disabled:opacity-50 text-text-primary shadow-2xs"
                      />
                    ))}
                  </div>

                  {/* Inline Error */}
                  {error && (
                    <div className="flex items-start space-x-2 p-2.5 bg-brand-red-light/30 border border-brand-red/10 rounded-lg text-brand-red text-xs leading-tight">
                      <AlertCircle className="w-4.5 h-4.5 shrink-0 mt-0.5" />
                      <span>{error}</span>
                    </div>
                  )}

                  {/* Verify button */}
                  <button
                    onClick={handleVerify}
                    disabled={loading || otp.some(d => d === "")}
                    className="w-full py-2.5 px-4 bg-brand-red text-white text-sm font-semibold rounded-lg shadow-sm hover:bg-brand-red-hover active:scale-[0.98] transition-all disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center space-x-2 cursor-pointer"
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Verifying...</span>
                      </>
                    ) : (
                      <span>Verify Code</span>
                    )}
                  </button>

                  {/* Resend and back navigation */}
                  <div className="flex items-center justify-between text-xs pt-2">
                    <button
                      onClick={() => setStep(1)}
                      disabled={loading}
                      className="text-text-secondary hover:text-brand-red font-medium transition-colors disabled:opacity-50 cursor-pointer flex items-center gap-1"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                      <span>Edit Email</span>
                    </button>
                    <div>
                      {canResend ? (
                        <button
                          onClick={handleResendOTP}
                          className="text-brand-red hover:underline font-semibold cursor-pointer"
                        >
                          Resend OTP
                        </button>
                      ) : (
                        <span className="text-text-muted">
                          Resend OTP in <span className="font-semibold text-text-secondary font-mono">{timer}s</span>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Professional Footer Statement */}
                  <div className="pt-4 mt-2 border-t border-border-primary/50 text-[11px] text-[#8C8C8C] leading-relaxed">
                    <p className="flex items-start gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 text-text-secondary shrink-0 mt-0.5" />
                      <span>
                        A 4-digit verification OTP has been sent. Please make sure to enter the code accurately. If you don't receive it within a few minutes, check your spam/junk folder.
                      </span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
